import { useEffect, useState, useCallback } from "react";
import { Loader2, Ticket, Download, Ban, Plus, Pencil, Trash2, History, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import api from "../../lib/api";

const STATUSES = ["", "active", "scheduled", "expired", "used", "used_up", "disabled", "revoked"];
const statusCls = (s) => ({
  active: "text-emerald-400", scheduled: "text-sky-400", expired: "text-white/40",
  used: "text-amber", used_up: "text-amber", disabled: "text-white/50", revoked: "text-firered",
}[s] || "text-white/60");
const inp = "bg-black/30 border-white/15 text-white";
const sel = "w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-1";

const EMPTY = {
  code: "", name: "", description: "", coupon_type: "public",
  assigned_email: "", discount_type: "percentage", discount_value: "",
  usage_per_account: "once", unlimited: true, total_usage_limit: "",
  valid_from: "", valid_until: "", min_spend: "", is_active: true,
};

const discountLabel = (v) => v.discount_type === "percentage" ? `${v.discount_value}%` : `£${Number(v.discount_value).toFixed(2)}`;
const usageLabel = (v) => v.coupon_type === "unique"
  ? "Unique (single use)"
  : v.usage_per_account === "multiple" ? "Multiple per account" : "Once per account";

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/vouchers", { params: { status: status || undefined, type: type || undefined, q: q || undefined } })
      .then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, [status, type, q]);
  useEffect(() => { load(); }, [load]);

  const num = (v) => (v === "" || v == null ? null : Number(v));
  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      code: v.code, name: v.name || "", description: v.description || "",
      coupon_type: v.coupon_type || "public", assigned_email: v.issued_to_email || "",
      discount_type: v.discount_type || "percentage", discount_value: v.discount_value ?? "",
      usage_per_account: v.usage_per_account || "once",
      unlimited: v.max_uses == null, total_usage_limit: v.max_uses ?? "",
      valid_from: (v.valid_from || "").slice(0, 10), valid_until: (v.valid_until || "").slice(0, 10),
      min_spend: v.min_spend ?? "", is_active: v.is_active !== false,
    });
    setOpen(true);
  };

  const payload = () => ({
    code: form.code.trim() || undefined, name: form.name.trim() || undefined,
    description: form.description.trim() || null,
    coupon_type: form.coupon_type,
    assigned_email: form.coupon_type === "unique" ? form.assigned_email.trim() : undefined,
    discount_type: form.discount_type, discount_value: num(form.discount_value),
    usage_per_account: form.usage_per_account,
    unlimited: form.unlimited, total_usage_limit: form.unlimited ? null : num(form.total_usage_limit),
    min_spend: num(form.min_spend),
    valid_from: form.valid_from ? new Date(form.valid_from + "T00:00:00").toISOString() : null,
    valid_until: form.valid_until ? new Date(form.valid_until + "T23:59:59").toISOString() : null,
    is_active: form.is_active,
  });

  const save = async () => {
    if (form.discount_value === "" || Number(form.discount_value) <= 0) { toast.error("Enter a discount value"); return; }
    if (form.coupon_type === "unique" && !form.assigned_email.trim()) { toast.error("Assign a customer email for a Unique Coupon"); return; }
    setSaving(true);
    try {
      if (editing) { await api.patch(`/admin/vouchers/${editing.voucher_id}`, payload()); toast.success("Coupon updated"); }
      else { await api.post("/admin/vouchers", payload()); toast.success("Coupon created"); }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save coupon"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (v) => {
    try { await api.patch(`/admin/vouchers/${v.voucher_id}`, { is_active: !(v.is_active !== false) }); load(); }
    catch { toast.error("Could not update"); }
  };
  const del = async (v) => {
    if (!window.confirm(`Delete coupon ${v.code}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/vouchers/${v.voucher_id}`); toast.success("Coupon deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  const reset = async (v) => {
    try { await api.post(`/admin/vouchers/${v.voucher_id}/reset`); toast.success("Coupon reset to unused"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reset"); }
  };
  const viewHistory = async (v) => {
    try { const { data } = await api.get(`/admin/vouchers/${v.voucher_id}/redemptions`); setHistory(data); }
    catch { toast.error("Could not load usage history"); }
  };
  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); };
  const exportCsv = () => window.open(`${api.defaults.baseURL}/admin/vouchers/export`, "_blank");

  return (
    <div className="space-y-6" data-testid="admin-coupons">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Coupons</h1>
          <p className="mt-1 text-muted-foreground text-sm">Create public or unique coupon codes, control usage per account and track redemptions.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-white/20 text-white gap-2" onClick={exportCsv} data-testid="export-coupons"><Download className="h-4 w-4" />Export</Button>
          <Button className="cf-gold-btn gap-2" onClick={openCreate} data-testid="create-coupon-btn"><Plus className="h-4 w-4" />Create Coupon</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search code, name or email…" value={q} onChange={(e) => setQ(e.target.value)} className={`${inp} max-w-xs`} data-testid="coupon-search" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm" data-testid="coupon-type-filter">
          <option value="">All types</option><option value="public">Public</option><option value="unique">Unique</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm" data-testid="coupon-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace("_", " ") : "All statuses"}</option>)}
        </select>
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground"><Ticket className="h-8 w-8 mx-auto text-gold/60" /><p className="mt-3">No coupons yet.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60 text-xs uppercase"><tr>
                <th className="text-left p-3">Code</th><th className="text-left p-3">Name</th><th className="text-left p-3">Type</th>
                <th className="text-left p-3">Discount</th><th className="text-left p-3">Usage</th>
                <th className="text-center p-3">Uses</th><th className="text-center p-3">Limit</th>
                <th className="text-left p-3">Start</th><th className="text-left p-3">Expiry</th>
                <th className="text-left p-3">Status</th><th className="text-center p-3">Active</th><th className="p-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.voucher_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`coupon-row-${v.code}`}>
                    <td className="p-3">
                      <button onClick={() => copyCode(v.code)} className="text-gold font-mono inline-flex items-center gap-1.5 hover:text-gold/80" data-testid={`copy-${v.code}`} title="Copy code">
                        {v.code} <Copy className="h-3 w-3 opacity-50" />
                      </button>
                    </td>
                    <td className="p-3 text-white/80 max-w-[160px] truncate">{v.name || "—"}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${v.coupon_type === "unique" ? "border-purple-400/40 text-purple-300" : "border-sky-400/40 text-sky-300"}`}>{v.coupon_type === "unique" ? "Unique" : "Public"}</span></td>
                    <td className="p-3 text-white/70">{discountLabel(v)}</td>
                    <td className="p-3 text-white/60 text-xs">{usageLabel(v)}</td>
                    <td className="p-3 text-center text-white/70">{v.uses_count || 0}</td>
                    <td className="p-3 text-center text-white/60">{v.max_uses ?? "∞"}</td>
                    <td className="p-3 text-white/50">{(v.valid_from || "").slice(0, 10) || "—"}</td>
                    <td className="p-3 text-white/50">{(v.valid_until || "").slice(0, 10) || "—"}</td>
                    <td className={`p-3 font-medium capitalize ${statusCls(v.status)}`}>{(v.status || "").replace("_", " ")}</td>
                    <td className="p-3 text-center"><Switch checked={v.is_active !== false} onCheckedChange={() => toggleActive(v)} data-testid={`coupon-active-${v.code}`} /></td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(v)} className="p-1.5 rounded hover:bg-white/10 text-white/70" title="Edit" data-testid={`edit-${v.code}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => viewHistory(v)} className="p-1.5 rounded hover:bg-white/10 text-white/70" title="Usage history" data-testid={`history-${v.code}`}><History className="h-3.5 w-3.5" /></button>
                        {v.coupon_type === "unique" && (v.status === "used" || (v.uses_count || 0) > 0) && (
                          <button onClick={() => reset(v)} className="p-1.5 rounded hover:bg-white/10 text-amber" title="Reset to unused" data-testid={`reset-${v.code}`}><RotateCcw className="h-3.5 w-3.5" /></button>
                        )}
                        <button onClick={() => del(v)} className="p-1.5 rounded hover:bg-firered/20 text-firered" title="Delete" data-testid={`delete-${v.code}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit coupon — ${editing.code}` : "Create coupon"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Coupon code {editing ? "" : "(blank = auto)"}</Label>
                <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="e.g. FIREWORKS10" className={`${inp} mt-1`} disabled={!!editing} data-testid="coupon-code" /></div>
              <div><Label className="text-white/80">Coupon name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className={`${inp} mt-1`} data-testid="coupon-name" /></div>
            </div>
            <div><Label className="text-white/80">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className={`${inp} mt-1`} data-testid="coupon-description" /></div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Coupon availability</Label>
                <select value={form.coupon_type} onChange={(e) => set("coupon_type", e.target.value)} className={sel} disabled={!!editing} data-testid="coupon-availability">
                  <option value="public">Public — shared by many customers</option>
                  <option value="unique">Unique — one specific customer</option>
                </select>
              </div>
              {form.coupon_type === "unique" ? (
                <div><Label className="text-white/80">Assigned customer email</Label><Input type="email" value={form.assigned_email} onChange={(e) => set("assigned_email", e.target.value)} placeholder="customer@email.com" className={`${inp} mt-1`} data-testid="coupon-assigned-email" /></div>
              ) : (
                <div><Label className="text-white/80">Usage per account</Label>
                  <select value={form.usage_per_account} onChange={(e) => set("usage_per_account", e.target.value)} className={sel} data-testid="coupon-usage-per-account">
                    <option value="once">Once per account</option>
                    <option value="multiple">Multiple uses per account</option>
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Discount type</Label>
                <select value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)} className={sel} data-testid="coupon-discount-type">
                  <option value="percentage">Percentage %</option><option value="fixed">Fixed £</option>
                </select>
              </div>
              <div><Label className="text-white/80">Discount value</Label><Input type="number" value={form.discount_value} onChange={(e) => set("discount_value", e.target.value)} className={`${inp} mt-1`} data-testid="coupon-discount-value" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Minimum reservation value £ (optional)</Label><Input type="number" value={form.min_spend} onChange={(e) => set("min_spend", e.target.value)} className={`${inp} mt-1`} data-testid="coupon-min-spend" /></div>
              <div>
                <Label className="text-white/80">Total usage limit</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Input type="number" value={form.total_usage_limit} onChange={(e) => set("total_usage_limit", e.target.value)} disabled={form.unlimited} placeholder={form.unlimited ? "Unlimited" : "e.g. 100"} className={`${inp} flex-1`} data-testid="coupon-total-limit" />
                  <label className="flex items-center gap-2 text-xs text-white/70 shrink-0"><Switch checked={form.unlimited} onCheckedChange={(v) => set("unlimited", v)} data-testid="coupon-unlimited" /> Unlimited</label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Start date (optional)</Label><Input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} className={`${inp} mt-1 [color-scheme:dark]`} data-testid="coupon-start-date" /></div>
              <div><Label className="text-white/80">Expiry date (optional)</Label><Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} className={`${inp} mt-1 [color-scheme:dark]`} data-testid="coupon-expiry-date" /></div>
            </div>

            <label className="flex items-center gap-2 text-sm text-white/80"><Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="coupon-active-toggle" /> Enabled</label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/20 text-white" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="cf-gold-btn" onClick={save} disabled={saving} data-testid="coupon-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save Changes" : "Create Coupon")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage history */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="bg-ink border-white/10 text-white max-w-2xl" data-testid="coupon-history-dialog">
          <DialogHeader><DialogTitle>Usage history — {history?.coupon?.code}</DialogTitle></DialogHeader>
          <div className="text-sm text-white/60 mb-2">Total redemptions: {history?.redemptions?.length || 0}{history?.coupon?.max_uses ? ` / ${history.coupon.max_uses}` : ""}</div>
          {(history?.redemptions || []).length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">Not used yet.</p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-white/50 text-xs uppercase"><tr>
                  <th className="text-left p-2">Customer</th><th className="text-left p-2">Reservation</th><th className="text-right p-2">Saved</th><th className="text-left p-2">Date</th><th className="text-left p-2"></th>
                </tr></thead>
                <tbody>
                  {history.redemptions.map((r) => (
                    <tr key={r.redemption_id} className="border-t border-white/5">
                      <td className="p-2 text-white/80">{r.customer_name || r.email || "—"}</td>
                      <td className="p-2 text-gold font-mono text-xs">{r.reservation_number}</td>
                      <td className="p-2 text-right text-emerald-400">£{Number(r.amount || 0).toFixed(2)}</td>
                      <td className="p-2 text-white/50">{(r.redeemed_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-2">{r.released && <span className="text-xs text-white/40">released</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
